import { useCallback, useEffect, useRef } from 'react';
import {
  v2SessionInfo,
  v2GetDivisions,
  v2Refresh,
  V2SessionInfoResponse,
  DivisionMembership,
} from 'src/store/modules/auth/v2Operations';
import { useLocation } from 'src/utils/Hooks';
import { getAuthVersion } from 'src/utils/authVersion';
import { saveDivisions, saveCurrentDivisionId } from 'src/utils/divisionStorage';
import { buildNoAccessUrl, buildSsoSignInUrl, getReferrerInfo, isSsoHost } from 'src/utils/ssoReferrer';

/**
 * Detail shape for tab-focus broadcasts: V2SessionInfoResponse
 * { authenticated, divisionId, reason? }. Lighter than the old
 * V2ValidateResponse -- consumers that need division metadata should look
 * it up in the localStorage cache via getDivisions().
 */
export const DIVISION_VALIDATED_EVENT = 'sl:division-validated';

const SSO_PATHS = ['/switch-division', '/sign_out', '/logout', '/auth/callback'];

/**
 * Hook that runs on mount and on tab focus to keep the V2 session in sync.
 *
 * Responsibilities:
 * - On mount: fetch divisions list (heavy, once per page load) + check
 *   liveness, sync to localStorage, redirect to /no-access if the active
 *   division doesn't grant access to the current app.
 * - On tab focus: lightweight session-info check only -- redirect to sign-in
 *   if token expired, auto-reload if user logged in from another tab.
 *
 * Division mismatch UI (ring + popup) is handled separately by
 * useDivisionMismatch / AccountDivisionPopover / OmniNavAccountLink, which
 * subscribe to DIVISION_VALIDATED_EVENT (V2SessionInfoResponse payload).
 *
 * Replaces the previous pattern of calling /v2/validate on every focus
 * event. See docs/auth-v2-followups.md (followup #0) in the api repo.
 */
export function useDivisionCheck(): void {
  const location = useLocation();

  const isCheckingRef = useRef(false);
  const lastCheckTimeRef = useRef(0);
  const hasInitializedRef = useRef(false);
  const wasInitiallyUnauthenticatedRef = useRef(false);
  const CHECK_DEBOUNCE_MS = 5000;
  const currentAppName = getReferrerInfo().appName;

  // V1 sessions don't have an sl-auth-token cookie, so v2SessionInfo would
  // always return {authenticated:false}. The visibility handler below would
  // then redirect a perfectly-valid V1 user back to /sign_in on tab focus.
  // Fold V1 into isSsoPage so both effects skip entirely for V1 sessions,
  // while still calling hooks in a consistent order.
  const isV1Session = getAuthVersion() === 'v1';
  const isOnSsoHost = typeof window !== 'undefined' && isSsoHost(window.location.host);
  const isSsoPage =
    isV1Session || isOnSsoHost || SSO_PATHS.some((path) => location.pathname.startsWith(path));

  const divisionHasAppAccess = useCallback((division: DivisionMembership | undefined): boolean => {
    // `enterprise-sso` is a defensive carve-out kept for safety — in practice
    // SSO hosts now resolve to `advisor` via HOSTNAME_TO_APP, so this branch
    // is only hit by an explicit app-name override.
    if (!division || currentAppName === 'enterprise-sso') {
      return true;
    }
    const accessLevel = division.appAccess?.[currentAppName];
    return Boolean(accessLevel) && accessLevel !== 'blocked';
  }, [currentAppName]);

  // True iff the division grants access to *any* non-blocked app. Used to
  // detect the "user has no usable Stackline access at all" terminal state
  // before we bounce them to /no-access. /no-access only knows how to route
  // them between apps/divisions; if every division is empty (e.g. a member
  // provisioned without an appAccess map), /no-access has nothing to do
  // and we can land in an app-to-app redirect loop. Short-circuit to
  // /sign_out instead.
  const divisionHasAnyAppAccess = (division: DivisionMembership | undefined): boolean => {
    if (!division?.appAccess) return false;
    return Object.values(division.appAccess).some(
      (level) => Boolean(level) && level !== 'blocked',
    );
  };

  const redirectToNoAccess = useCallback((divisionName: string | undefined) => {
    // `buildNoAccessUrl` forwards the page-host stage (`dev`/`qa`/`localhost`)
    // to the SSO via `?stage=` so cross-stage redirects land back on the
    // matching stage's accessible app instead of always defaulting to prod
    // (the SSO host is shared between dev and prod, so it can't otherwise
    // tell). Prod traffic emits no `stage` param — URL is unchanged.
    window.location.href = buildNoAccessUrl(currentAppName, divisionName || 'your division');
  }, [currentAppName]);

  useEffect(() => {
    if (isSsoPage) {
      return;
    }

    const captureInitialDivision = async () => {
      try {
        let [sessionInfo, divisionsResp] = await Promise.all([
          v2SessionInfo(),
          v2GetDivisions().catch(() => null),
        ]);

        // Page-load refresh fallback: session-info returns HTTP 200 with
        // authenticated:false when the access JWT is missing/expired, so the
        // axios 401 interceptor never fires for it. If the refresh cookie is
        // still valid (typical 30-day window), one /v2/refresh call rotates
        // the access JWT server-side and a re-check sees authenticated:true.
        // This is what saves the user from a forced sign-in on page reload.
        if (!sessionInfo.authenticated) {
          try {
            await v2Refresh();
            [sessionInfo, divisionsResp] = await Promise.all([
              v2SessionInfo(),
              v2GetDivisions().catch(() => null),
            ]);
          } catch (_refreshErr) {
            // Refresh failed; fall through to the existing unauthenticated
            // branch below, which marks wasInitiallyUnauthenticatedRef.
          }
        }

        if (sessionInfo.authenticated && sessionInfo.divisionId) {
          hasInitializedRef.current = true;
          wasInitiallyUnauthenticatedRef.current = false;
          saveCurrentDivisionId(sessionInfo.divisionId);
          if (divisionsResp?.divisions) {
            saveDivisions(divisionsResp.divisions);
          }

          // appAccess gate: look up the active division in the freshly-fetched
          // divisions list and bounce to /no-access if the current app isn't
          // granted.
          const activeDivision = divisionsResp?.divisions?.find(
            d => d.id === sessionInfo.divisionId,
          );
          if (activeDivision && !divisionHasAppAccess(activeDivision)) {
            // Before bouncing to /no-access, confirm there is *somewhere*
            // for /no-access to send the user. If no division anywhere
            // grants any non-blocked app (e.g. membership row written
            // without an appAccess map), /no-access has nothing usable
            // and we'd end up looping between apps. Sign out directly.
            const allDivisions = divisionsResp?.divisions ?? [];
            const hasAnyUsableApp = allDivisions.some(divisionHasAnyAppAccess);
            if (!hasAnyUsableApp) {
              window.location.href = '/sign_out';
              return;
            }
            const displayName = activeDivision.companyName
              ? `${activeDivision.companyName} ${activeDivision.name}`
              : activeDivision.name;
            redirectToNoAccess(displayName);
          }
        } else {
          hasInitializedRef.current = true;
          wasInitiallyUnauthenticatedRef.current = true;
        }
      } catch (error) {
        // Treat a transient initial failure as "unauthenticated for now".
        // Without this, hasInitializedRef stays false and checkDivision early-
        // returns forever, so tab focus does nothing for the rest of the session.
        // Marking unauthenticated lets the next successful focus check either
        // reload (if the user has since logged in elsewhere) or redirect to
        // sign-in (if they're still not authenticated).
        console.error('[useDivisionCheck] Failed to capture initial division:', error);
        hasInitializedRef.current = true;
        wasInitiallyUnauthenticatedRef.current = true;
      }
    };

    captureInitialDivision();
  }, [isSsoPage]);

  const checkDivision = useCallback(async (): Promise<void> => {
    if (isSsoPage || !hasInitializedRef.current) {
      return;
    }

    const now = Date.now();
    if (now - lastCheckTimeRef.current < CHECK_DEBOUNCE_MS) {
      return;
    }
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;
    lastCheckTimeRef.current = now;

    try {
      let response = await v2SessionInfo();

      // session-info returns HTTP 200 with `authenticated:false` when the
      // access JWT is missing/expired -- so the axios 401 interceptor never
      // fires for it. Try /v2/refresh once before bouncing to sign-in. If
      // the sl-refresh-token cookie is still valid (typical 30-day window),
      // the access JWT is rotated server-side via Set-Cookie and a follow-up
      // session-info will see authenticated:true. Only redirect if refresh
      // also fails (revoked / expired refresh token / Cognito refused).
      if (!response.authenticated) {
        try {
          await v2Refresh();
          response = await v2SessionInfo();
        } catch (_refreshErr) {
          // Refresh failed -- session is genuinely dead. Fall through to
          // the redirect below.
        }
      }

      if (wasInitiallyUnauthenticatedRef.current && response.authenticated) {
        window.location.reload();
        return;
      }

      if (!response.authenticated) {
        window.location.href = buildSsoSignInUrl(window.location.href);
        return;
      }

      window.dispatchEvent(new CustomEvent<V2SessionInfoResponse>(DIVISION_VALIDATED_EVENT, { detail: response }));
    } catch (error) {
      console.error('[useDivisionCheck] Failed to check session:', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [isSsoPage]);

  useEffect(() => {
    if (isSsoPage) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkDivision();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSsoPage, checkDivision]);
}

export default useDivisionCheck;
